import { Button, InputNumber, Space } from 'antd';
import { useState } from 'react';

import {
    OnCallShiftDtoStipendPayoutOptionEnum,
    ShiftListItemDto,
    ShiftType,
} from '@clh/api-client';
import { useApiClient } from '@clh/ui';
import { CostCalculator, roundToAtMostTwoDecimals } from '@clh/util';

import { ActionButton } from '../../action-button';
import { useSelection } from '../../hooks/use-selection';

const EditShiftRate = ({
    payRate: defaultPayRate,
    totalRate: defaultTotalRate,
    close,
}: {
    payRate: number;
    totalRate: number;
    close: () => void;
}) => {
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const api = useApiClient();

    const [payRate, setPayRate] = useState<number | null>(
        roundToAtMostTwoDecimals(defaultPayRate / 100)
    );
    const [totalRate, setTotalRate] = useState<number | null>(
        roundToAtMostTwoDecimals(defaultTotalRate / 100)
    );

    const [onCallPayRate, setOnCallPayRate] = useState<number | null>(
        selection.onCallShift?.payRate
            ? roundToAtMostTwoDecimals(selection.onCallShift.payRate / 100)
            : null
    );
    const [onCallTotalRate, setOnCallTotalRate] = useState<number | null>(
        selection.onCallShift?.totalRate
            ? roundToAtMostTwoDecimals(selection.onCallShift.totalRate / 100)
            : null
    );

    const onEditPayRate = (rate: number | null) => {
        setPayRate(rate);

        setTotalRate(
            roundToAtMostTwoDecimals(
                Number(rate) *
                    (1 + selection.platformFeePct) *
                    (1 + selection.employerFeePct)
            )
        );
    };

    const onEditTotalRate = (rate: number | null) => {
        setTotalRate(rate);

        const rates = CostCalculator.getCostsPerHour(
            Number(rate) * 100,
            selection.platformFeePct,
            selection.employerFeePct
        );

        setPayRate(roundToAtMostTwoDecimals(rates.payRate / 100));
    };

    const onEditOnCallPayRate = (rate: number | null) => {
        setOnCallPayRate(rate);

        setOnCallTotalRate(
            roundToAtMostTwoDecimals(
                Number(rate) *
                    (1 + selection.platformFeePct) *
                    (1 + selection.employerFeePct)
            )
        );
    };

    const onEditOnCallTotalRate = (rate: number | null) => {
        setOnCallTotalRate(rate);

        const rates = CostCalculator.getCostsPerHour(
            Number(rate) * 100,
            selection.platformFeePct,
            selection.employerFeePct
        );

        setOnCallPayRate(roundToAtMostTwoDecimals(rates.payRate / 100));
    };

    return (
        <Space direction="vertical" style={{ width: '300px' }}>
            <Space direction="horizontal">
                <div>
                    {selection.shiftType === ShiftType.OnCall
                        ? 'Called-in pay rate:'
                        : 'Pay rate:'}
                    <InputNumber value={payRate} onChange={onEditPayRate} />
                </div>
                <div>
                    {selection.shiftType === ShiftType.OnCall
                        ? 'Called-in total rate:'
                        : 'Total rate:'}
                    <InputNumber value={totalRate} onChange={onEditTotalRate} />
                </div>
            </Space>

            {selection.shiftType === ShiftType.OnCall && (
                <Space direction="horizontal">
                    <div>
                        On-call pay rate:
                        <InputNumber
                            value={onCallPayRate}
                            onChange={onEditOnCallPayRate}
                        />
                    </div>
                    <div>
                        On-call total rate:
                        <InputNumber
                            value={onCallTotalRate}
                            onChange={onEditOnCallTotalRate}
                        />
                    </div>
                </Space>
            )}

            <Space direction="horizontal">
                <ActionButton
                    disabled={!payRate || payRate === 0}
                    type="primary"
                    successMessage="Rates updated"
                    action={async () => {
                        if (!payRate || !totalRate) {
                            throw Error('Invalid pay rate entered');
                        }

                        if (
                            selection.shiftType === ShiftType.OnCall &&
                            (!onCallTotalRate || onCallTotalRate === 0)
                        ) {
                            throw Error('Invalid on-call rate entered');
                        }

                        const updatePayload: any = {
                            payRate: payRate * 100,
                            totalRate: totalRate * 100,
                        };

                        if (
                            selection.shiftType === ShiftType.OnCall &&
                            onCallTotalRate
                        ) {
                            updatePayload.onCallShift = {
                                totalRate: onCallTotalRate * 100,
                                minutesWorked:
                                    selection.onCallShift?.minutesWorked ??
                                    null,
                            };
                        }

                        await api?.shiftControllerUpdateShift({
                            shiftId: selection.id,
                            shiftUpdateDto: updatePayload,
                        });

                        let updatedOnCallShift = selection.onCallShift;
                        if (
                            selection.shiftType === ShiftType.OnCall &&
                            onCallPayRate &&
                            onCallTotalRate
                        ) {
                            updatedOnCallShift = {
                                totalRate: onCallTotalRate * 100,
                                payRate: onCallPayRate * 100,
                                minutesWorked:
                                    selection.onCallShift?.minutesWorked ??
                                    null,
                                stipendPayoutOption:
                                    selection.onCallShift
                                        ?.stipendPayoutOption ??
                                    OnCallShiftDtoStipendPayoutOptionEnum.Always,
                                createdAt: new Date(),
                                updatedAt: new Date(),
                            };
                        }

                        setSelection({
                            ...selection,
                            payRate: payRate * 100,
                            totalRate: totalRate * 100,
                            onCallShift: updatedOnCallShift,
                        });
                        close();
                    }}
                >
                    Save
                </ActionButton>
                <Button onClick={close}>Cancel</Button>
            </Space>
        </Space>
    );
};

export default EditShiftRate;
